import { CONSTANTS } from './constants.js';

// Håndterer prioritering af energikilder og strategier
export class PriorityManager {
  constructor(storageManager, constraints) {
    this.storage = storageManager;
    this.constraints = constraints;
    this.heaterUsedCapacity = 0; // Track heater usage in current hour
    this.heaterMode = null; // null | 'LTES' | 'HTES'
    this.htesOperation = null; // null | 'charge' | 'discharge'
    this.forecast = null; // injected reference
    this.ltesChargeAny = false; // any LTES charge (waste or heater) this hour
  }
  
  // Reset heater capacity for new hour
  resetHourlyTracking() {
    this.heaterUsedCapacity = 0;
    this.heaterMode = null;
    this.htesOperation = null;
    this.ltesChargeAny = false;
  }
  
  // Check if we're in arbitrage time windows
  getTimeWindows(hour) {
    const chargeWindow = CONSTANTS.TIME_WINDOWS.arbitrage_charge;
    const dischargeWindow = CONSTANTS.TIME_WINDOWS.arbitrage_discharge;
    
    return {
      isChargeTime: hour >= chargeWindow.start && hour <= chargeWindow.end,
      isDischargeTime: hour >= dischargeWindow.start && hour <= dischargeWindow.end
    };
  }
  
  // Calculate energy deficit after considering storage
  calculateDeficit(demand) {
    const available = this.storage.getAvailableForDemand();
    return Math.max(0, demand - available.total);
  }
  
  // PRIORITET 1: Brug eksisterende lager
  useStorageForDemand(demand, hour, spotPrice) {
    const actions = [];
    let remainingDemand = demand;
    let production = 0;
    const { isDischargeTime } = this.getTimeWindows(hour);
    
    // Fra LTES først (altid tilgængelig)
    const ltesAvailable = this.storage.getAvailableForDemand().ltes;
    if (remainingDemand > 0 && ltesAvailable > 0) {
      const ltesSupply = Math.min(remainingDemand, ltesAvailable);
      const actualSupply = this.storage.dischargeLTES(ltesSupply);
      
      if (actualSupply > 0) {
        remainingDemand -= actualSupply;
        production += actualSupply;
        
        actions.push({
          type: 'DISCHARGE_LTES_TO_DH',
          amount: actualSupply,
          source: 'LTES',
          remainingDemand: remainingDemand
        });
      }
    }
    
    // AGGRESSIVE: Run turbine when spot price > 150 DKK (covers waste heat cost)
    const shouldUseTurbine = (isDischargeTime || spotPrice > 150) 
                             && remainingDemand > 0 && this.canDischargeHTES();
    
    if (shouldUseTurbine) {
      const available = this.storage.getAvailableForDemand();
      const turbineInput = Math.min(
        remainingDemand / CONSTANTS.EFFICIENCY.turbine_heat,
        available.turbineInput
      );
      
      if (turbineInput > 0) {
        const actualInput = this.storage.dischargeHTES(turbineInput);
        const heatProduced = actualInput * CONSTANTS.EFFICIENCY.turbine_heat;
        const electricityProduced = actualInput * CONSTANTS.EFFICIENCY.turbine_electric;
        this.markHTESDischarge();
        
        // Heat to LTES then to demand
        const ltesSpace = this.storage.getAvailableSpace().ltes;
        const heatToLTES = Math.min(heatProduced, ltesSpace);
        if (heatToLTES > 0) {
          this.storage.chargeLTES(heatToLTES);
        }
        
        remainingDemand -= heatProduced;
        production += heatProduced;
        
        actions.push({
          type: 'DISCHARGE_HTES_TURBINE',
          input: actualInput,
          heat: heatProduced,
          electricity: electricityProduced,
          category: isDischargeTime ? 'ARBITRAGE' : 'OPERATIONAL'
        });
      }
    }
    
    return {
      actions,
      production,
      remainingDemand
    };
  }
  
  // Request heater usage for a specific mode (LTES or HTES), returns allowable amount
  // CRITICAL: Enforces Heliac rule - HTES and LTES can NEVER charge simultaneously
  requestHeaterUse(mode, requestedAmount) {
    const available = this.constraints.heater_max_MW - this.heaterUsedCapacity;
    if (available <= 0) return 0;
    
    // HELIAC RULE: If heater is already in use for other storage, deny request
    if (this.heaterMode && this.heaterMode !== mode) {
      // PERFORMANCE: Comment out verbose logging
      // console.log(`HELIAC RULE: Denied ${mode} charge - heater already in ${this.heaterMode} mode`);
      return 0;
    }

    // Cannot charge HTES while discharging it
    if (mode === 'HTES' && this.htesOperation === 'discharge') {
      // PERFORMANCE: Comment out verbose logging
      // console.log('HELIAC RULE: Cannot charge HTES while discharging');
      return 0;
    }

    // HELIAC RULE: Cannot charge LTES with heater if HTES is charging
    if (mode === 'LTES' && this.heaterMode === 'HTES') {
      // PERFORMANCE: Comment out verbose logging
      // console.log('HELIAC RULE: Cannot charge LTES while HTES is charging');
      return 0;
    }

    // HELIAC RULE: Cannot charge HTES if LTES is charging with heater
    if (mode === 'HTES' && this.heaterMode === 'LTES') {
      // PERFORMANCE: Comment out verbose logging
      // console.log('HELIAC RULE: Cannot charge HTES while LTES is charging');
      return 0;
    }
    
    return Math.max(0, Math.min(requestedAmount, available));
  }

  // Commit heater usage and lock mode if not already set
  // CRITICAL: Once committed, mode is locked for the hour
  commitHeaterUse(mode, usedAmount) {
    if (usedAmount <= 0) return;
    
    // HELIAC RULE: Lock the heater mode for this hour
    if (!this.heaterMode) {
      this.heaterMode = mode;
      // PERFORMANCE: Comment out verbose logging
      // console.log(`HELIAC: Heater locked to ${mode} mode for this hour`);
    } else if (this.heaterMode !== mode) {
      console.error(`HELIAC VIOLATION: Attempted to commit ${mode} but heater is in ${this.heaterMode} mode!`);
      return; // Reject the commit
    }
    
    this.heaterUsedCapacity += usedAmount;
  }

  // HTES operation exclusivity helpers - HELIAC RULES
  canChargeHTES() {
    // Cannot charge if:
    // 1. HTES is currently discharging
    // 2. Heater is locked to LTES mode
    const canCharge = this.htesOperation !== 'discharge' &&
                     this.heaterMode !== 'LTES';
    // PERFORMANCE: Comment out verbose logging
    // if (!canCharge) {
    //   console.log(`HELIAC: HTES charge blocked - htesOp: ${this.htesOperation}, heaterMode: ${this.heaterMode}`);
    // }
    return canCharge;
  }
  
  canDischargeHTES() {
    // Cannot discharge if HTES is currently charging
    const canDischarge = this.htesOperation !== 'charge';
    // PERFORMANCE: Comment out verbose logging
    // if (!canDischarge) {
    //   console.log(`HELIAC: HTES discharge blocked - htesOp: ${this.htesOperation}`);
    // }
    return canDischarge;
  }
  
  // LTES charging check - can charge with waste heat even if heater is busy
  canChargeLTESWithWaste() {
    // Waste heat can always charge LTES (no heater needed)
    return true;
  }
  
  canChargeLTESWithHeater() {
    // Cannot charge LTES with heater if heater is locked to HTES
    return this.heaterMode !== 'HTES';
  }

  // LTES any charge marker (waste or heater)
  markLTESChargeAny() {
    this.ltesChargeAny = true;
  }
  markHTESCharge() {
    if (!this.htesOperation) this.htesOperation = 'charge';
  }
  markHTESDischarge() {
    if (!this.htesOperation) this.htesOperation = 'discharge';
  }

  // PRIORITY 2: Waste heat - ALWAYS accept (cheapest at 150 DKK/MWh)
  useWasteHeat(wasteHeatMW, forceCharge = false) {
    const levels = this.storage.getLevels();
    const space = this.storage.getAvailableSpace();
    
    // ALWAYS use waste heat - even at 99% full (make space if needed)
    if (wasteHeatMW > 0 && (forceCharge || levels.ltes < 0.995)) {
      const wasteToUse = Math.min(wasteHeatMW, space.ltes);
      
      if (wasteToUse > 0.001) { // Lower threshold
        const stored = this.storage.chargeLTES(wasteToUse);
        
        return {
          used: wasteToUse,
          stored: stored,
          cost: wasteToUse * CONSTANTS.FACTORY_WASTE_HEAT_PRICE
        };
      }
    }
    
    return { used: 0, stored: 0, cost: 0 };
  }
  
  // PRIORITY 3: Solar energy - cheaper than grid when excluding TSO!
  useSolar(solarMWh, deficit, spotPrice) {
    const space = this.storage.getAvailableSpace();
    const heaterAvailable = this.constraints.heater_max_MW - this.heaterUsedCapacity;
    
    // Use ALL available solar - it's cheaper without TSO fees
    if (solarMWh > 0 && heaterAvailable > 0) {
      // Take as much solar as possible, not just deficit
      const solarNeeded = Math.min(
        solarMWh,
        heaterAvailable,
        Math.max(space.ltes, space.htes) // Use either storage
      );
      
      if (solarNeeded > 0.001) { // Lower threshold
        const stored = this.storage.chargeLTES(solarNeeded, CONSTANTS.EFFICIENCY.heater_to_ltes);
        this.heaterUsedCapacity += solarNeeded;
        
        return {
          used: solarNeeded,
          stored: stored,
          cost: solarNeeded * Math.max(0, spotPrice) // Ingen TSO på sol
        };
      }
    }
    
    return { used: 0, stored: 0, cost: 0 };
  }
  
  // PRIORITET 4: Grid (sidste udvej)
  useGrid(deficit, spotPrice) {
    const space = this.storage.getAvailableSpace();
    const heaterAvailable = this.constraints.heater_max_MW - this.heaterUsedCapacity;
    
    if (deficit > 0 && heaterAvailable > 0) {
      const gridNeeded = Math.min(
        heaterAvailable,
        space.ltes,
        deficit / CONSTANTS.EFFICIENCY.heater_to_ltes
      );
      
      if (gridNeeded > 0.1) {
        const stored = this.storage.chargeLTES(gridNeeded, CONSTANTS.EFFICIENCY.heater_to_ltes);
        this.heaterUsedCapacity += gridNeeded;
        
        return {
          used: gridNeeded,
          stored: stored,
          cost: gridNeeded * (Math.max(0, spotPrice) + CONSTANTS.TSO_AFGIFT)
        };
      }
    }
    
    return { used: 0, stored: 0, cost: 0 };
  }
  
  // Arbitrage strategier - ONLY BATTERY (3MW capacity, 366 cycles/year)
  executeArbitrage(hour, spotPrice, solarMWh) {
    const actions = [];
    
    // ARBITRAGE = ONLY battery operations
    // All HTES operations have been moved to OPERATIONAL category
    // Battery arbitrage is handled directly in SimulationEngineV2 with daily cycle guarantee
    
    return actions;
  }
  
  // Check if heater capacity allows simultaneous HTES/LTES charging
  canChargeSimultaneously(htesAmount, ltesAmount) {
    const totalRequired = htesAmount + ltesAmount;
    const available = this.constraints.heater_max_MW - this.heaterUsedCapacity;
    return totalRequired <= available;
  }
}

export default PriorityManager;